/**
 * EVO — diller kanali va bonus API klienti.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ PUL — `string`, HAR JOYDA                                                ║
 * ║                                                                          ║
 * ║ `amountBase`, `balanceBase`, `amount`, `pointValueBase` — hammasi MATN   ║
 * ║ va shunday QOLADI. `Number()` ga aylantirish `lib/format.ts`             ║
 * ║ sarlavhasida taqiqlangan: `0.011134 × 8` suzuvchi nuqtada               ║
 * ║ `0.08907199999999999` beradi va u kassadan chiqadigan so'mga aylanadi.  ║
 * ║                                                                          ║
 * ║ BALL esa butun son (`points`, `pointsBalance`) — u pul emas va u bilan   ║
 * ║ arifmetika xavfsiz. Ikkalasi FAQAT serverda uchrashadi                   ║
 * ║ (`bonus.support.ts#pointsToMoney`), brauzerda hech qachon.               ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
import { api } from './api';
import type { Page } from './products';

// ---------------------------------------------------------------------------
// Diller
// ---------------------------------------------------------------------------

export const DEALER_STATUSES = ['active', 'suspended', 'terminated'] as const;
export type DealerStatus = (typeof DEALER_STATUSES)[number];

export interface Dealer {
  readonly id: number;
  readonly code: string;
  readonly name: string;
  readonly customerId: number;
  readonly customerCode: string;
  readonly customerName: string;
  readonly territory: string | null;
  readonly contractNo: string | null;
  readonly contractFrom: string | null;
  readonly contractTo: string | null;
  readonly status: DealerStatus;
  readonly isActive: boolean;
  readonly version: number;
}

export interface DealerParams {
  readonly page: number;
  readonly pageSize: number;
  readonly q?: string | undefined;
  readonly status?: DealerStatus | undefined;
}

export function fetchDealers(params: DealerParams, signal?: AbortSignal): Promise<Page<Dealer>> {
  return api.get<Page<Dealer>>('/dealers', {
    query: { page: params.page, pageSize: params.pageSize, q: params.q, status: params.status },
    ...(signal ? { signal } : {}),
  });
}

export interface DealerCreateInput {
  readonly customerId: number;
  readonly code: string;
  readonly name: string;
  readonly territory?: string;
  readonly contractNo?: string;
}

export function createDealer(input: DealerCreateInput): Promise<Dealer> {
  return api.post<Dealer>('/dealers', input);
}

export function updateDealerStatus(id: number, status: DealerStatus): Promise<Dealer> {
  return api.patch<Dealer>(`/dealers/${id}`, { status });
}

// ---------------------------------------------------------------------------
// Rebate pog'onalari
// ---------------------------------------------------------------------------

export interface RebateTier {
  readonly id: number;
  readonly year: number;
  readonly fromAmountBase: string;
  readonly rebatePct: string;
  readonly notes: string | null;
  readonly isActive: boolean;
}

export function fetchRebateTiers(
  dealerId: number,
  year: number,
  signal?: AbortSignal,
): Promise<readonly RebateTier[]> {
  return api.get<readonly RebateTier[]>(`/dealers/${dealerId}/rebate-tiers`, {
    query: { year },
    ...(signal ? { signal } : {}),
  });
}

/**
 * Upsert javobi — RO'YXAT SATRIDAN TOR.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ NIMA UCHUN `RebateTier` EMAS                                             │
 * │                                                                          │
 * │ Server `onConflictDoUpdate(...).returning(...)` da FAQAT to'rt ustunni   │
 * │ qaytaradi (`dealers.service.ts#upsertTier`): `id`, `year`,               │
 * │ `fromAmountBase`, `rebatePct`. `notes` va `isActive` javobda YO'Q.       │
 * │                                                                          │
 * │ `RebateTier` deb e'lon qilish tipni yolg'onga aylantirardi: ekran        │
 * │ `tier.isActive` ni o'qib, `undefined` ni `false` deb tushunib «pog'ona   │
 * │ o'chirilgan» degan nishonni chiqarardi — SAQLANGAN pog'ona uchun.        │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export interface RebateTierUpsertResult {
  readonly id: number;
  readonly year: number;
  readonly fromAmountBase: string;
  readonly rebatePct: string;
}

export function upsertRebateTier(
  dealerId: number,
  input: { year: number; fromAmountBase: string; rebatePct: string },
): Promise<RebateTierUpsertResult> {
  return api.post<RebateTierUpsertResult>(`/dealers/${dealerId}/rebate-tiers`, input);
}

// ---------------------------------------------------------------------------
// Bonus
// ---------------------------------------------------------------------------

export const BONUS_PARTY_TYPES = ['dealer', 'installer'] as const;
export type BonusPartyType = (typeof BONUS_PARTY_TYPES)[number];

export interface BonusAccount {
  readonly partyType: BonusPartyType;
  readonly partyId: number;
  readonly code: string;
  readonly name: string;
  readonly accruedBase: string;
  readonly paidBase: string;
  readonly balanceBase: string;
  /** Faqat usta uchun — dillerda `null`. */
  readonly pointsBalance: number | null;
  readonly isBlocked: boolean;
}

export interface BonusAccountParams {
  readonly page: number;
  readonly pageSize: number;
  readonly q?: string | undefined;
  readonly partyType?: BonusPartyType | undefined;
  readonly withBalance?: boolean | undefined;
}

export function fetchBonusAccounts(
  params: BonusAccountParams,
  signal?: AbortSignal,
): Promise<Page<BonusAccount>> {
  return api.get<Page<BonusAccount>>('/bonus/accounts', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      q: params.q,
      partyType: params.partyType,
      // Faqat `true` yuboriladi: `false` «qoldiqsizlarni ko'rsat» degani emas,
      // «filtr yo'q» degani va u umuman yuborilmasligi kerak.
      ...(params.withBalance === true ? { withBalance: 'true' } : {}),
    },
    ...(signal ? { signal } : {}),
  });
}

export interface BonusEntry {
  readonly id: number;
  readonly docNo: string;
  readonly partyType: BonusPartyType;
  readonly partyId: number;
  readonly entryType: 'accrual' | 'payout';
  readonly periodYear: number | null;
  readonly basisAmountBase: string;
  readonly rebatePct: string;
  readonly points: number;
  readonly pointValueBase: string;
  readonly amountBase: string;
  readonly balanceAfter: string;
  readonly currencyCode: string | null;
  readonly amount: string | null;
  readonly paidAt: string | null;
  readonly cashAccountId: number | null;
  readonly notes: string | null;
  readonly occurredAt: string;
  readonly isReversal: boolean;
}

export interface BonusEntryParams {
  readonly page: number;
  readonly pageSize: number;
  readonly partyType?: BonusPartyType | undefined;
  readonly entryType?: 'accrual' | 'payout' | undefined;
  readonly q?: string | undefined;
}

export function fetchBonusEntries(
  params: BonusEntryParams,
  signal?: AbortSignal,
): Promise<Page<BonusEntry>> {
  return api.get<Page<BonusEntry>>('/bonus/entries', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      partyType: params.partyType,
      entryType: params.entryType,
      q: params.q,
    },
    ...(signal ? { signal } : {}),
  });
}

/** Bir ballning USD qiymati. `null` — katalogdan hisoblab bo'lmadi. */
export function fetchPointValue(signal?: AbortSignal): Promise<{ pointValueBase: string | null }> {
  return api.get<{ pointValueBase: string | null }>('/bonus/point-value', {
    ...(signal ? { signal } : {}),
  });
}

export function accrueBonus(input: { dealerId: number; year: number }): Promise<BonusEntry> {
  return api.post<BonusEntry>('/bonus/accrue', input);
}

export interface BonusPayoutInput {
  readonly partyType: BonusPartyType;
  readonly partyId: number;
  readonly cashAccountId: number;
  /** Usta uchun — ball soni. */
  readonly points?: number;
  /** Diller uchun — USD summa (MATN). */
  readonly amountBase?: string;
  readonly notes?: string;
}

export function payoutBonus(input: BonusPayoutInput): Promise<BonusEntry> {
  return api.post<BonusEntry>('/bonus/payout', input);
}

// ---------------------------------------------------------------------------
// To'langan bonusni teskarilash (storno)
// ---------------------------------------------------------------------------

/**
 * Sababning eng qisqa uzunligi — `EXPENSE_REVERSAL_MIN_REASON` va
 * `PAYMENT_REVERSAL_MIN_REASON` bilan AYNAN bir xil.
 *
 * Uchtasi bir xil bo'lishi shart: bir ekranda qabul qilingan matn boshqasida
 * rad etilsa, foydalanuvchi buni tushuntira olmasdi. Serverda ham chegara
 * uchta qatlamda takrorlangan (zod → xizmat → `ck_bonus_entries_reversal_
 * reason`), bu esa TO'RTINCHI nusxa va u hech qaysisining o'rnini bosmaydi.
 */
export const BONUS_REVERSAL_MIN_REASON = 5;

/**
 * Teskarilash javobidagi bonus satri.
 *
 * `BonusEntry` (ro'yxat shakli) QAYTA ISHLATILMADI: server bu yerda
 * jadvalning TO'LIQ satrini qaytaradi (`entryById()` → `select()`), ya'ni
 * ro'yxatda yo'q maydonlar ham keladi — birinchi navbatda `reversalReason`
 * va `reversedById`. Ro'yxat tipini kengaytirish esa teskarisini qilardi:
 * `/bonus/entries` javobida bu maydonlar YO'Q va tip yolg'on va'da berardi.
 */
export interface BonusReversalEntry {
  readonly id: number;
  readonly docNo: string;
  readonly partyType: BonusPartyType;
  readonly partyId: number;
  readonly entryType: 'accrual' | 'payout';
  readonly points: number;
  readonly amountBase: string;
  readonly balanceAfter: string;
  readonly currencyCode: string | null;
  readonly amount: string | null;
  readonly paidAt: string | null;
  readonly isReversal: boolean;
  readonly reversalOfId: number | null;
  readonly reversedById: number | null;
  readonly reversalReason: string | null;
}

/**
 * `POST /bonus/:id/reverse` javobi.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ `pointsRestored` — XARAJAT STORNOSIDA YO'Q BO'LGAN MAYDON                ║
 * ║                                                                          ║
 * ║ Bonus stornosi IKKI narsani qaytaradi: pulni kassaga va BALLNI ustaga.   ║
 * ║ Ikkinchisi ekranda alohida ko'rsatilishi SHART — «pul qaytdi» degan      ║
 * ║ yagona jumlani o'qigan odam ballning ham qaytganini bilmaydi va ustaga   ║
 * ║ «balansingiz tiklandi» deb ayta olmaydi. Usta esa ertasiga ilovada       ║
 * ║ aynan o'sha raqamni ko'radi.                                             ║
 * ║                                                                          ║
 * ║ Dillerda u har doim `0` — dillerning bonusi ballda o'lchanmaydi.         ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * Tayyor `message` maydoni ekranda ISHLATILMAYDI — undagi raqamlar
 * formatlanmagan (`lib/payments.ts` dagi `PaymentReversal` bilan bir xil
 * sabab).
 */
export interface BonusReversal {
  /** Teskarilangan asl to'lov — endi `reversedById` to'ldirilgan. */
  readonly original: BonusReversalEntry;
  /** Yaratilgan storno hujjati — `isReversal: true`, kassa harakati `in`. */
  readonly reversal: BonusReversalEntry;
  readonly cashAccountId: number;
  readonly cashBalanceBefore: string;
  readonly cashBalanceAfter: string;
  /** Ustaga qaytarilgan ball. Dillerda `0`. */
  readonly pointsRestored: number;
  readonly message: string;
}

/**
 * To'langan bonusni teskarilaydi — pul kassaga, ball ustaga QAYTADI.
 *
 * Ruxsat `dealer.bonus.reverse` va u `dealer.bonus.payout` dan ALOHIDA:
 * bitta kodda bo'lsa, o'sha odam «to'la → qaytar → yana to'la» halqasini
 * o'z qo'lida yopib qo'yardi (`bonus.controller.ts` dagi ramka).
 */
export function reverseBonus(id: number, reason: string): Promise<BonusReversal> {
  return api.post<BonusReversal>(`/bonus/${id}/reverse`, { reason });
}

// ---------------------------------------------------------------------------
// Kassa hisoblari — to'lov formasidagi tanlov ro'yxati
// ---------------------------------------------------------------------------

export interface CashAccountOption {
  readonly id: number;
  readonly code: string;
  readonly name: string;
  readonly currencyCode: string;
  readonly currentBalance: string;
  readonly isActive?: boolean;
}

/**
 * Kassalar ro'yxati — `GET /api/cash-accounts` (`cashaccount.view`).
 *
 * Alohida tor endpoint QILINMADI (`expenses.flowItems()` dan farqli):
 * bonusni to'laydigan odamda `dealer.bonus.payout` bor va u FAQAT
 * moliyachi/adminda — ikkalasida `cashaccount.view` ham bor. Ya'ni bu yerda
 * `expenses` dagi «kassirda DDS ruxsati yo'q» muammosi umuman yuzaga
 * kelmaydi.
 */
export function fetchCashAccounts(signal?: AbortSignal): Promise<readonly CashAccountOption[]> {
  return api.get<readonly CashAccountOption[]>('/cash-accounts', {
    ...(signal ? { signal } : {}),
  });
}

// ---------------------------------------------------------------------------
// Query kalitlari
// ---------------------------------------------------------------------------

export const dealerKeys = {
  all: ['dealer'] as const,
  dealers: (params: DealerParams) => ['dealer', 'dealers', params] as const,
  tiers: (dealerId: number, year: number) => ['dealer', 'tiers', dealerId, year] as const,
  bonusAccounts: (params: BonusAccountParams) => ['dealer', 'bonus-accounts', params] as const,
  bonusEntries: (params: BonusEntryParams) => ['dealer', 'bonus-entries', params] as const,
  pointValue: ['dealer', 'point-value'] as const,
  cashAccounts: ['dealer', 'cash-accounts'] as const,
};
